(function () {
  'use strict';

  const instances = new WeakMap();
  const modulo = (value, length) => ((value % length) + length) % length;

  function toArray(value) {
    return Array.prototype.slice.call(value || []);
  }

  function ensureLazyLoader() {
    if (window.lazySizes && window.lazySizes.loader) return;

    const unveil = (element) => {
      if (!element || element.dataset.lazyLoaded === 'true') return;

      const source = element.getAttribute('data-src');
      const sourceSet = element.getAttribute('data-srcset');
      const sizes = element.getAttribute('data-sizes');

      if (sizes && sizes !== 'auto') element.setAttribute('sizes', sizes);
      if (sourceSet) element.setAttribute('srcset', sourceSet);
      if (source) element.setAttribute('src', source);

      element.dataset.lazyLoaded = 'true';
      element.classList.remove('lazyload', 'lazyloading');
      element.classList.add('lazyloaded');
      element.dispatchEvent(new Event('lazyloaded', { bubbles: true }));
    };

    const checkElems = () => {
      document.querySelectorAll('.lazyload').forEach((element) => {
        if (observer) {
          observer.observe(element);
        } else {
          unveil(element);
        }
      });
    };

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          unveil(entry.target);
        });
      }, { rootMargin: '250px 0px' })
      : null;

    window.lazySizes = {
      loader: {
        unveil,
        checkElems
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', checkElems);
    } else {
      checkElems();
    }
  }

  class NativeCarousel {
    constructor(element, options) {
      this.element = typeof element === 'string' ? document.querySelector(element) : element;
      this.options = Object.assign({
        wrapAround: false,
        pageDots: false,
        prevNextButtons: false,
        adaptiveHeight: false,
        fade: false,
        cellAlign: 'left',
        cellSelector: '.carousel__slide',
        initialIndex: 0,
        rightToLeft: false,
        autoPlay: false,
        draggable: true,
        skipHiddenCells: false,
        unveilAll: false,
        fullWidthSlides: false,
        responsiveColumns: false,
        on: {}
      }, options || {});

      if (!this.element) return;

      this.events = {};
      this.player = { state: 'stopped' };
      this.selectedIndex = 0;
      this.selectedElement = null;
      this.cells = [];
      this.slides = this.cells;
      this._destroyers = [];
      this._resizeFrame = null;

      Object.keys(this.options.on || {}).forEach((eventName) => {
        this.on(eventName, this.options.on[eventName]);
      });

      instances.set(this.element, this);
      this.activate();
    }

    activate() {
      this.element.classList.add('flickity-enabled', 'is-draggable');
      if (this.options.fade) this.element.classList.add('is-fade');
      if (!this.element.hasAttribute('role')) this.element.setAttribute('role', 'region');
      if (!this.element.hasAttribute('aria-roledescription')) this.element.setAttribute('aria-roledescription', 'carousel');
      this.build();
      this.reloadCells();
      this.bind();
      this.select(this.getInitialIndex(), true, true);
      this.emit('ready');
      if (this.options.autoPlay) this.playPlayer();
      requestAnimationFrame(() => this.resize());
    }

    build() {
      this.viewport = this.element.querySelector(':scope > .flickity-viewport');
      this.slider = this.viewport ? this.viewport.querySelector(':scope > .flickity-slider') : null;

      if (this.viewport && this.slider) return;

      let slides = toArray(this.element.querySelectorAll(`:scope > ${this.options.cellSelector}`));
      if (!slides.length) {
        slides = toArray(this.element.querySelectorAll(this.options.cellSelector));
      }
      this.viewport = document.createElement('div');
      this.viewport.className = 'flickity-viewport';
      this.slider = document.createElement('div');
      this.slider.className = 'flickity-slider';

      this.viewport.appendChild(this.slider);
      const firstControl = this.element.querySelector(':scope > .flickity-nav, :scope > .flickity-custom-dots, :scope > .flickity-custom-navigation');
      this.element.insertBefore(this.viewport, firstControl || null);
      slides.forEach((slide) => this.slider.appendChild(slide));
      this.element.querySelectorAll(':scope > .cart-recommendations-track:empty').forEach((track) => track.remove());
    }

    bind() {
      this.prevButton = this.element.querySelector('.flickity-prev');
      this.nextButton = this.element.querySelector('.flickity-next');

      if (this.options.prevNextButtons !== false && this.prevButton) {
        const previous = (event) => {
          if (event.type === 'keyup' && !['Enter', ' '].includes(event.key)) return;
          event.preventDefault();
          this.previous();
        };
        this.prevButton.addEventListener('click', previous);
        this.prevButton.addEventListener('keyup', previous);
        this._destroyers.push(() => {
          this.prevButton.removeEventListener('click', previous);
          this.prevButton.removeEventListener('keyup', previous);
        });
      }

      if (this.options.prevNextButtons !== false && this.nextButton) {
        const next = (event) => {
          if (event.type === 'keyup' && !['Enter', ' '].includes(event.key)) return;
          event.preventDefault();
          this.next();
        };
        this.nextButton.addEventListener('click', next);
        this.nextButton.addEventListener('keyup', next);
        this._destroyers.push(() => {
          this.nextButton.removeEventListener('click', next);
          this.nextButton.removeEventListener('keyup', next);
        });
      }

      const onResize = () => {
        this.applyCellStyles();
        this.resize();
      };
      window.addEventListener('resize', onResize);
      this._destroyers.push(() => window.removeEventListener('resize', onResize));

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.scheduleResize());
      }

      this.bindMeasurements();
      this.bindDrag();
      this.bindAutoplayPause();
    }

    bindMeasurements() {
      const onContentReady = (event) => {
        if (event.target && event.target.closest && event.target.closest('.flickity-viewport') !== this.viewport) return;
        this.scheduleResize();
      };

      this.element.addEventListener('load', onContentReady, true);
      this.element.addEventListener('lazyloaded', onContentReady, true);
      this._destroyers.push(() => {
        this.element.removeEventListener('load', onContentReady, true);
        this.element.removeEventListener('lazyloaded', onContentReady, true);
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleResize());
        this.resizeObserver.observe(this.element);
        if (this.viewport) this.resizeObserver.observe(this.viewport);
        if (this.slider) this.resizeObserver.observe(this.slider);
        this.cells.forEach((cell) => this.resizeObserver.observe(cell.element));
        this._destroyers.push(() => this.resizeObserver.disconnect());
      }
    }

    bindDrag() {
      if (this.options.draggable === false || !this.viewport) return;

      let startX = 0;
      let startY = 0;
      let lastX = 0;
      let deltaX = 0;
      let isPointerDown = false;
      let isDragging = false;
      let didDrag = false;
      let pointerId = null;
      let startedAt = 0;

      const start = (event) => {
        if (this.cells.length < 2) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        isPointerDown = true;
        isDragging = false;
        pointerId = event.pointerId;
        deltaX = 0;
        startedAt = Date.now();
        startX = this.getPointerX(event);
        startY = this.getPointerY(event);
        lastX = startX;
      };

      const move = (event) => {
        if (!isPointerDown) return;
        const currentX = this.getPointerX(event);
        const currentY = this.getPointerY(event);
        const moveX = currentX - startX;
        const moveY = currentY - startY;

        if (!isDragging && Math.abs(moveY) > Math.abs(moveX) && Math.abs(moveY) > 10) {
          isPointerDown = false;
          pointerId = null;
          return;
        }

        if (!isDragging && Math.abs(moveX) > 8 && Math.abs(moveX) > Math.abs(moveY)) {
          isDragging = true;
          didDrag = true;
          if (this.slider) this.slider.style.transition = 'none';
          if (this.viewport.setPointerCapture && pointerId != null) {
            try {
              this.viewport.setPointerCapture(pointerId);
            } catch (err) { /* Pointer capture can fail if the browser already released it. */ }
          }
          this.viewport.classList.add('is-pointer-down');
        }

        if (!isDragging) return;
        deltaX = this.getDragDelta(moveX);
        lastX = currentX;
        event.preventDefault();
        this.updateDraggedPosition(deltaX);
      };

      const end = (event) => {
        if (!isPointerDown) return;
        isPointerDown = false;

        if (!isDragging) {
          pointerId = null;
          return;
        }

        isDragging = false;
        if (this.viewport.releasePointerCapture && pointerId != null) {
          try {
            this.viewport.releasePointerCapture(pointerId);
          } catch (err) { /* Pointer may already be released by the browser. */ }
        }
        pointerId = null;
        this.viewport.classList.remove('is-pointer-down');
        if (this.slider) {
          this.slider.style.transition = 'transform 520ms cubic-bezier(0.22, 1, 0.36, 1)';
        }

        const elapsed = Math.max(Date.now() - startedAt, 1);
        const velocity = Math.abs(lastX - startX) / elapsed;
        const threshold = Math.min(Math.max((this.viewport.clientWidth || 320) * 0.18, 36), 96);

        if (Math.abs(deltaX) < threshold && velocity < 0.5) {
          this.updatePosition();
          window.setTimeout(() => { didDrag = false; }, 250);
          return;
        }
        this.select(this.getDragTargetIndex(deltaX));
        window.setTimeout(() => { didDrag = false; }, 250);
      };

      const click = (event) => {
        if (!didDrag) return;
        didDrag = false;
        event.preventDefault();
        event.stopPropagation();
      };

      this.viewport.style.touchAction = 'pan-y';
      this.viewport.addEventListener('pointerdown', start);
      window.addEventListener('pointermove', move, { passive: false });
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
      this.viewport.addEventListener('click', click, true);

      this._destroyers.push(() => {
        this.viewport.removeEventListener('pointerdown', start);
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
        this.viewport.removeEventListener('click', click, true);
      });
    }

    updateDraggedPosition(deltaX) {
      if (!this.slider || !this.selectedElement || this.options.fade) return;

      const x = this.getSelectedOffset();
      const direction = this.options.rightToLeft ? 1 : -1;
      this.slider.style.transform = `translate3d(${direction * x + deltaX}px, 0, 0)`;
    }

    bindAutoplayPause() {
      if (!this.options.autoPlay) return;

      const stop = () => this.stopPlayer();
      const play = () => this.playPlayer();

      this.element.addEventListener('mouseenter', stop);
      this.element.addEventListener('focusin', stop);
      this.element.addEventListener('mouseleave', play);
      this.element.addEventListener('focusout', play);

      this._destroyers.push(() => {
        this.element.removeEventListener('mouseenter', stop);
        this.element.removeEventListener('focusin', stop);
        this.element.removeEventListener('mouseleave', play);
        this.element.removeEventListener('focusout', play);
      });
    }

    getPointerX(event) {
      return event.touches && event.touches.length ? event.touches[0].clientX : event.clientX;
    }

    getPointerY(event) {
      return event.touches && event.touches.length ? event.touches[0].clientY : event.clientY;
    }

    getDragDelta(deltaX) {
      const atStart = this.selectedIndex <= 0;
      const atEnd = this.selectedIndex >= this.getMaxSelectableIndex();
      const pullingPastStart = atStart && (this.options.rightToLeft ? deltaX < 0 : deltaX > 0);
      const pullingPastEnd = atEnd && (this.options.rightToLeft ? deltaX > 0 : deltaX < 0);

      return pullingPastStart || pullingPastEnd ? deltaX * 0.28 : deltaX;
    }

    getDragTargetIndex(deltaX) {
      const shouldGoNext = this.options.rightToLeft ? deltaX > 0 : deltaX < 0;
      let step = 1;

      if (this.options.responsiveColumns) {
        const cellWidth = this.selectedElement?.offsetWidth || this.cells[this.selectedIndex]?.size?.width || this.viewport?.clientWidth || 1;
        step = Math.max(1, Math.round(Math.abs(deltaX) / Math.max(cellWidth, 1)));
      }

      const targetIndex = this.selectedIndex + (shouldGoNext ? step : -step);
      return Math.max(0, Math.min(targetIndex, this.getMaxSelectableIndex()));
    }

    getInitialIndex() {
      if (typeof this.options.initialIndex === 'string') {
        const selected = this.element.querySelector(this.options.initialIndex);
        const index = this.cells.findIndex((cell) => cell.element === selected);
        return index > -1 ? index : 0;
      }
      return Number(this.options.initialIndex) || 0;
    }

    reloadCells() {
      const selector = this.options.cellSelector || '.carousel__slide';
      let slides = toArray(this.slider.querySelectorAll(selector));

      if (this.options.fullWidthSlides) {
        slides.forEach((slide) => {
          slide.style.left = '';
          slide.style.transform = '';
        });
      }

      if (this.options.skipHiddenCells) {
        slides = slides.filter((slide) => {
          const styles = window.getComputedStyle(slide);
          return styles.display !== 'none' && styles.visibility !== 'hidden';
        });
      }

      this.cells = slides.map((slide) => ({
        element: slide,
        size: {
          width: slide.offsetWidth,
          height: slide.offsetHeight
        }
      }));
      this.slides = this.cells;

      this.applyCellStyles();
      this.buildDots();
      this.updateButtons();
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver.observe(this.element);
        if (this.viewport) this.resizeObserver.observe(this.viewport);
        if (this.slider) this.resizeObserver.observe(this.slider);
        this.cells.forEach((cell) => this.resizeObserver.observe(cell.element));
      }
      this.scheduleResize();
      return this.cells;
    }

    applyCellStyles() {
      if (!this.slider) return;

      this.slider.style.display = this.options.fade ? 'block' : 'flex';
      this.slider.style.alignItems = 'stretch';
      this.slider.style.justifyContent = 'flex-start';
      this.slider.style.position = 'relative';
      this.slider.style.height = this.options.fade ? '100%' : 'auto';
      this.slider.style.transition = this.options.fade ? '' : 'transform 520ms cubic-bezier(0.22, 1, 0.36, 1)';
      this.slider.style.willChange = this.options.fade ? '' : 'transform';

      this.cells.forEach((cell) => {
        const slide = cell.element;
        const productCellWidth = this.getResponsiveProductCellWidth(slide);
        slide.style.left = '';
        slide.style.transform = '';

        if (this.options.fade) {
          slide.style.position = 'absolute';
          slide.style.inset = '0';
          slide.style.width = '100%';
          slide.style.opacity = '0';
          slide.style.transition = 'opacity 520ms cubic-bezier(0.22, 1, 0.36, 1)';
        } else {
          slide.style.position = 'relative';
          if (this.options.fullWidthSlides) {
            slide.style.flex = '0 0 100%';
            slide.style.maxWidth = '100%';
            slide.style.width = '100%';
          } else if (productCellWidth) {
            slide.style.flex = `0 0 ${productCellWidth}`;
            slide.style.maxWidth = productCellWidth;
            slide.style.width = productCellWidth;
          } else {
            slide.style.flexShrink = '0';
          }
          slide.style.opacity = '';
          slide.style.transition = '';
        }
      });
    }

    getResponsiveProductCellWidth(slide) {
      if (!this.options.responsiveColumns || !slide.classList.contains('columns')) {
        return null;
      }

      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const breakpoints = viewportWidth >= 1068 ? ['large', 'medium', 'small'] : viewportWidth >= 768 ? ['medium', 'small'] : ['small'];

      for (const breakpoint of breakpoints) {
        const columns = this.getClassNumber(this.element, `${breakpoint}-up-`);
        if (columns) return `${100 / columns}%`;

        const span = this.getGridSpan(slide, breakpoint);
        if (span) return span;
      }

      return null;
    }

    getClassNumber(element, prefix) {
      const className = element.className || '';
      const match = className.match(new RegExp(`(?:^|\\s)${prefix}(\\d+)(?:\\s|$)`));
      return match ? parseInt(match[1], 10) : null;
    }

    getGridSpan(element, breakpoint) {
      const className = element.className || '';
      if (new RegExp(`(?:^|\\s)${breakpoint}-1\\/5(?:\\s|$)`).test(className)) {
        return '20%';
      }

      const columns = this.getClassNumber(element, `${breakpoint}-`);
      return columns ? `${(columns / 12) * 100}%` : null;
    }

    buildDots() {
      if (!this.options.pageDots) {
        const existingDots = this.element.querySelector(':scope > .flickity-page-dots');
        if (existingDots) existingDots.remove();
        this.dots = [];
        return;
      }

      let dotsList = this.element.querySelector(':scope > .flickity-page-dots');
      if (!dotsList) {
        dotsList = document.createElement('ol');
        dotsList.className = 'flickity-page-dots';
        this.element.appendChild(dotsList);
      }

      dotsList.innerHTML = '';
      this.dots = this.cells.map((cell, index) => {
        const dot = document.createElement('li');
        dot.className = 'dot';
        dot.setAttribute('aria-label', `Slide ${index + 1}`);
        dot.setAttribute('tabindex', '0');
        dot.addEventListener('click', () => this.select(index));
        dot.addEventListener('keyup', (event) => {
          if (['Enter', ' '].includes(event.key)) this.select(index);
        });
        dotsList.appendChild(dot);
        return dot;
      });
    }

    select(target, isInstant, suppressEvent) {
      if (!this.cells.length) return;

      let index = this.resolveIndex(target);
      if (index < 0) return;

      const maxSelectableIndex = this.getMaxSelectableIndex();

      if (this.options.wrapAround) {
        index = modulo(index, maxSelectableIndex + 1);
      } else {
        index = Math.max(0, Math.min(index, maxSelectableIndex));
      }

      const previousIndex = this.selectedIndex;
      this.selectedIndex = index;
      this.selectedElement = this.cells[index].element;

      this.updatePosition(isInstant);
      this.updateSelectedClasses(previousIndex);
      this.updateButtons();
      this.unveilActiveImages();
      this.resize();
      this.emit('scroll', this.cells.length > 1 ? index / (this.cells.length - 1) : 1);

      if (!suppressEvent && previousIndex !== index) {
        this.emit('change', index);
      }

      this.emit('settle', index);
    }

    scheduleResize() {
      if (this._resizeFrame) return;
      this._resizeFrame = requestAnimationFrame(() => {
        this._resizeFrame = null;
        this.resize();
      });
    }

    resolveIndex(target) {
      if (typeof target === 'number') return target;
      if (typeof target === 'string') {
        const element = this.element.querySelector(target);
        return this.cells.findIndex((cell) => cell.element === element);
      }
      if (target && target.nodeType === 1) {
        return this.cells.findIndex((cell) => cell.element === target);
      }
      return 0;
    }

    updatePosition(isInstant) {
      if (!this.slider || !this.selectedElement) return;

      if (this.options.fade) {
        this.cells.forEach((cell, index) => {
          cell.element.style.opacity = index === this.selectedIndex ? '1' : '0';
        });
        return;
      }

      const x = this.getSelectedOffset();
      const direction = this.options.rightToLeft ? 1 : -1;
      const previousTransition = this.slider.style.transition;

      if (isInstant) this.slider.style.transition = 'none';
      this.slider.style.transform = `translate3d(${direction * x}px, 0, 0)`;

      if (isInstant) {
        requestAnimationFrame(() => {
          this.slider.style.transition = previousTransition || 'transform 520ms cubic-bezier(0.22, 1, 0.36, 1)';
        });
      }
    }

    getSelectedOffset() {
      if (!this.selectedElement) return 0;

      if (this.options.fullWidthSlides) {
        const offsetLeft = this.selectedElement.offsetLeft || 0;
        if (offsetLeft > 0 || this.selectedIndex === 0) return offsetLeft;

        return this.cells.slice(0, this.selectedIndex).reduce((offset, cell) => {
          return offset + (cell.element.offsetWidth || this.viewport?.clientWidth || 0);
        }, 0);
      }

      let x = this.selectedElement.offsetLeft || 0;
      if (this.options.cellAlign === 'center' && this.viewport) {
        x -= Math.max((this.viewport.clientWidth - this.selectedElement.offsetWidth) / 2, 0);
      }

      return Math.max(x, 0);
    }

    updateSelectedClasses(previousIndex) {
      this.cells.forEach((cell, index) => {
        const isSelected = index === this.selectedIndex;
        cell.element.classList.toggle('is-selected', isSelected);
        cell.element.classList.toggle('is-initial-selected', isSelected);
        cell.element.setAttribute('aria-hidden', String(!isSelected));
      });

      if (this.dots) {
        this.dots.forEach((dot, index) => dot.classList.toggle('is-selected', index === this.selectedIndex));
      }

      if (previousIndex != null && this.cells[previousIndex]) {
        this.cells[previousIndex].element.classList.remove('is-previous');
      }
    }

    updateButtons() {
      const maxSelectableIndex = this.getMaxSelectableIndex();
      const atStart = this.selectedIndex <= 0;
      const atEnd = this.selectedIndex >= maxSelectableIndex;
      const disabled = this.cells.length < 2;

      if (this.prevButton) {
        const isDisabled = disabled || (!this.options.wrapAround && atStart);
        this.prevButton.classList.toggle('is-disabled', isDisabled);
        this.prevButton.toggleAttribute('disabled', isDisabled);
        this.prevButton.setAttribute('aria-disabled', String(isDisabled));
      }

      if (this.nextButton) {
        const isDisabled = disabled || (!this.options.wrapAround && atEnd);
        this.nextButton.classList.toggle('is-disabled', isDisabled);
        this.nextButton.toggleAttribute('disabled', isDisabled);
        this.nextButton.setAttribute('aria-disabled', String(isDisabled));
      }
    }

    resize() {
      if (!this.viewport) return;

      this.cells.forEach((cell) => {
        cell.size = {
          width: cell.element.offsetWidth,
          height: cell.element.offsetHeight
        };
      });

      if (!this.options.fade && !this.options.adaptiveHeight) {
        this.viewport.style.height = 'auto';
        this.updatePosition();
        return;
      }

      const maxHeight = this.cells.reduce((height, cell) => Math.max(height, cell.element.offsetHeight), 0);
      const selectedHeight = this.selectedElement ? this.selectedElement.offsetHeight : 0;
      const viewportHeight = this.options.adaptiveHeight ? selectedHeight : maxHeight || selectedHeight;

      if (viewportHeight) {
        this.viewport.style.height = `${viewportHeight}px`;
      }

      this.updatePosition();
      this.unveilActiveImages();
    }

    getMaxSelectableIndex() {
      if (!this.cells.length) return 0;
      if (this.options.fade || !this.options.contain || !this.viewport || !this.options.responsiveColumns) {
        return this.cells.length - 1;
      }

      const viewportWidth = this.viewport.clientWidth || 0;
      if (!viewportWidth) return this.cells.length - 1;

      const lastCell = this.cells[this.cells.length - 1].element;
      const maxOffset = Math.max(0, (lastCell.offsetLeft + lastCell.offsetWidth) - viewportWidth);
      let maxIndex = this.cells.findIndex((cell) => cell.element.offsetLeft >= maxOffset - 1);

      if (maxIndex === -1) {
        maxIndex = this.cells.length - 1;
      }

      return Math.max(0, Math.min(maxIndex, this.cells.length - 1));
    }

    unveilActiveImages() {
      const indexes = this.options.unveilAll
        ? this.cells.map((_, index) => index)
        : [this.selectedIndex, this.selectedIndex + 1, this.selectedIndex - 1]
          .map((index) => modulo(index, this.cells.length));

      indexes.forEach((index) => {
        const slide = this.cells[index] && this.cells[index].element;
        if (!slide) return;
        slide.querySelectorAll('img.lazyload, img.lazyloading, img[data-src], img[data-srcset]').forEach((image) => this.unveilImage(image));
      });
    }

    unveilImage(image) {
      if (!image) return;
      if (image.dataset.nativeCarouselLazyLoaded === 'true') return;

      if (window.lazySizes && window.lazySizes.loader && typeof window.lazySizes.loader.unveil === 'function') {
        window.lazySizes.loader.unveil(image);
      }

      const source = image.getAttribute('data-src');
      const sourceSet = image.getAttribute('data-srcset');
      const sizes = image.getAttribute('data-sizes');
      const fallbackSource = sourceSet ? sourceSet.trim().split(/\s+/)[0] : '';

      if (sizes) {
        image.setAttribute('sizes', sizes === 'auto' ? `${Math.max(image.offsetWidth || this.viewport?.clientWidth || 1, 1)}px` : sizes);
      }
      if (sourceSet) {
        image.setAttribute('srcset', sourceSet);
      }
      if (source || fallbackSource) {
        image.setAttribute('src', source || fallbackSource);
      }

      image.classList.remove('lazyload', 'lazyloading');
      image.classList.add('lazyloaded');
      image.dataset.lazyLoaded = 'true';
      image.dataset.nativeCarouselLazyLoaded = 'true';
      image.dispatchEvent(new Event('lazyloaded', { bubbles: true }));
    }

    next() {
      this.select(this.selectedIndex + 1);
    }

    previous() {
      this.select(this.selectedIndex - 1);
    }

    selectCell(target) {
      this.select(target);
    }

    getCell(element) {
      return this.cells.find((cell) => cell.element === element) || null;
    }

    getCellElements() {
      return this.cells.map((cell) => cell.element);
    }

    getCellSlideIndex(cell) {
      return this.cells.indexOf(cell);
    }

    insert() {
      this.reloadCells();
    }

    updateDraggable() {}

    playPlayer() {
      if (!this.options.autoPlay || this.cells.length < 2) return;
      this.stopPlayer();
      this.player.state = 'playing';
      this._autoplayTimer = window.setInterval(() => this.next(), parseInt(this.options.autoPlay, 10));
    }

    stopPlayer() {
      if (this._autoplayTimer) {
        window.clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
      this.player.state = 'paused';
    }

    unpausePlayer() {
      this.playPlayer();
    }

    on(eventName, callback) {
      if (typeof callback !== 'function') return;
      this.events[eventName] = this.events[eventName] || [];
      this.events[eventName].push(callback);
    }

    emit(eventName, ...args) {
      (this.events[eventName] || []).forEach((callback) => callback.apply(this, args));
    }

    destroy() {
      this.stopPlayer();
      if (this._resizeFrame) {
        cancelAnimationFrame(this._resizeFrame);
        this._resizeFrame = null;
      }
      this._destroyers.forEach((destroy) => destroy());
      this._destroyers = [];
      if (this.viewport && this.slider) {
        this.cells.forEach((cell) => {
          cell.element.removeAttribute('style');
          this.element.insertBefore(cell.element, this.viewport);
        });
        this.viewport.remove();
      }
      const dots = this.element.querySelector(':scope > .flickity-page-dots');
      if (dots) dots.remove();
      this.element.classList.remove('flickity-enabled', 'is-draggable', 'is-fade');
      instances.delete(this.element);
    }
  }

  function get(element) {
    const target = typeof element === 'string' ? document.querySelector(element) : element;
    return target ? instances.get(target) : undefined;
  }

  ensureLazyLoader();

  window.ThemeCarousel = {
    Carousel: NativeCarousel,
    get,
    modulo
  };

})();
